import { autoUpdate, computePosition, flip, offset, type Placement } from '@floating-ui/dom'
import classNames from 'classnames'
import { useSelect } from 'downshift'
import type { ComponentChild } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import styles from './index.module.scss'

type Item<T> = {
  value: T
  label: ComponentChild
  description?: string | null
  disabled?: boolean
}

type DropdownProps<T> = {
  offset?: number
  width?: number
  value?: T
  options: Item<T>[]
  className?: string
  placement?: Placement
  onChange: (value: T) => void
  children: (props: {
    isOpen: boolean
    selectedItem: Item<T> | null
    setRef: (el: HTMLElement | null) => void
    triggerProps: ReturnType<typeof useSelect>['getToggleButtonProps']
  }) => preact.ComponentChildren
}

export const Dropdown = <T,>(props: DropdownProps<T>) => {
  const triggerRef = useRef<HTMLElement>(null)
  const floatingRef = useRef<HTMLDivElement>(null)

  const select = useSelect({
    items: props.options,
    selectedItem: props.options.find(item => item.value == props.value) ?? null,
    onSelectedItemChange: e => {
      if (e.selectedItem && !e.selectedItem.disabled) {
        props.onChange(e.selectedItem.value)
      }
    },
  })

  useEffect(() => {
    if (!select.isOpen || !triggerRef.current || !floatingRef.current) {
      floatingRef.current?.classList.remove(styles.visible)
      return
    }

    const trigger = triggerRef.current
    const floating = floatingRef.current

    return autoUpdate(trigger, floating, () => {
      computePosition(trigger, floating, {
        placement: props.placement ?? 'bottom-start',
        middleware: [flip(), offset(props.offset)],
      }).then(({ x, y }) => {
        floating.style.left = `${x}px`
        floating.style.top = `${y}px`
        floating.classList.add(styles.visible)
      })
    })
  }, [select.isOpen, props.placement])

  return (
    <>
      {props.children({
        isOpen: select.isOpen,
        selectedItem: select.selectedItem,
        setRef: el => triggerRef.current = el,
        triggerProps: select.getToggleButtonProps,
      })}

      {select.isOpen && (
        <div
          {...select.getMenuProps({ ref: floatingRef })}
          class={styles.list}
          style={{
            maxWidth: props.width,
          }}
        >
          {props.options.map((item, index) => (
            <button
              key={String(item.value)}
              {...select.getItemProps({ item, index })}
              class={classNames(
                styles.item,
                select.highlightedIndex === index && styles.hover,
                select.selectedItem === item && styles.selected,
                item.disabled ? styles.disabled : undefined,
              )}
            >
              <div class={styles.label}>{item.label}</div>
              {item.description && <div class={styles.desc}>{item.description}</div>}
            </button>
          ))}
        </div>
      )}
    </>
  )
}
